import type { MutableRefObject } from 'react';
import { AppDispatch } from '../../store';
import { getParameterFromURL } from '../scripts';
import { getCurrencySymbol } from '../scripts/currency';
import { setPaymentProduct, setTariff } from '../../reduxStore/data/actions';
import { markDiscountShown, showPaymentModal, toggleLoader, updatePurchaseFlow } from '../../reduxStore/navigation/actions';
import EveryDoggyAPIServices from '../services/EveryDoggyAPIServices';
import { extractEmailFromUrl } from '../scripts/extractEmailFromUrl';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMEvent, GTMOnboardingFunnelEvent, GTMQuizFunnelEvent } from '../analytics';
import { PAYPAL_AMPLITUDE_DEVICE_ID_QUERY_PARAM } from './paypalAmplitudeDeviceGate';

interface HandlePaypalRedirectParams {
  dispatch: AppDispatch;
  productName: string;
  /** Comes from the loosely-typed Redux `paymentModal` state, so it is effectively untyped at the source. */
  productAmount: any;
  /** Ref that guards against double-handling the redirect within the component lifecycle. */
  handledRef: MutableRefObject<boolean>;
}

/**
 * Handles the PayPal redirect return/cancel flow:
 * reads the `paypal_*` URL params, restores product/tariff state, fires the
 * success/cancel analytics + purchase flow, and clears the redirect params.
 *
 * Extracted verbatim from the `App` component effect — behaviour is unchanged.
 */
export function handlePaypalRedirect({ dispatch, productName, productAmount, handledRef }: HandlePaypalRedirectParams): void {
  if (handledRef.current) {
    return;
  }

  const redirectStatus = getParameterFromURL('paypal_redirect_status');

  if (!redirectStatus) {
    return;
  }

  if (redirectStatus === 'cancel' || redirectStatus === 'return') {
    const paywallSource = redirectStatus === 'return' ? 'paypal_return' : 'cancel';
    try {
      window.sessionStorage.setItem('paypal_show_payment_page_source', paywallSource);
    } catch (_) {
      void 0;
    }
  }

  const clearPaypalRedirectParams = () => {
    const url = new URL(window.location.href);
    [
      'paypal_redirect_status',
      PAYPAL_AMPLITUDE_DEVICE_ID_QUERY_PARAM,
      'subscription_id',
      'token',
      'ba_token',
      'PayerID',
      'paypal_product_id',
      'paypal_product_name',
      'paypal_product_amount',
      'paypal_product_old_price',
      'paypal_redirect_flow',
      'paypal_tariff',
      'paypal_discount_shown',
    ].forEach(key => url.searchParams.delete(key));
    window.history.replaceState({}, document.title, url.toString());
  };

  const currency = getCurrencySymbol();

  const redirectProductId = getParameterFromURL('paypal_product_id') || '';
  const redirectProductName = getParameterFromURL('paypal_product_name') || '';
  const redirectProductAmount = getParameterFromURL('paypal_product_amount') || '';
  const redirectProductOldPrice = getParameterFromURL('paypal_product_old_price') || '';
  const redirectFlow = getParameterFromURL('paypal_redirect_flow') || 'default';
  const redirectTariff = getParameterFromURL('paypal_tariff') || '';
  const redirectDiscountShown = getParameterFromURL('paypal_discount_shown') === '1';
  const isChatUpsalePaypalFlow = redirectFlow === 'chat_upsale';

  const resolvedProductName = redirectProductName || productName;
  const resolvedProductAmount = redirectProductAmount || productAmount;
  const resolvedPriceNumber = Number(resolvedProductAmount);

  if (redirectProductId) {
    try {
      window.sessionStorage.setItem('paypal_restore_product_id', redirectProductId);
    } catch (_) {
      void 0;
    }

    const restoredProduct = {
      product: redirectProductId,
      name: redirectProductName || resolvedProductName || '',
      ...(redirectProductOldPrice ? { oldPrice: redirectProductOldPrice } : {}),
    };
    dispatch(setPaymentProduct(JSON.stringify(restoredProduct)));
  }

  if (redirectTariff) {
    dispatch(setTariff(redirectTariff));
  }

  if (redirectDiscountShown) {
    dispatch(markDiscountShown());
  }

  if (redirectStatus === 'cancel') {
    if (!resolvedProductName || !resolvedProductAmount) {
      return;
    }

    handledRef.current = true;
    if (isChatUpsalePaypalFlow) {
      dispatch(
        updatePurchaseFlow({
          email: extractEmailFromUrl(),
          payment_system: 'paypal',
          screenFlow: ['ChatUpsale'],
          startFromScreen: 'ChatUpsale',
        })
      );
    }
    dispatch(
      showPaymentModal({
        product: resolvedProductName,
        currency,
        price: Number.isFinite(resolvedPriceNumber) ? resolvedPriceNumber : 0,
      })
    );
    clearPaypalRedirectParams();
    return;
  }

  if (redirectStatus !== 'return') {
    handledRef.current = true;
    clearPaypalRedirectParams();
    return;
  }

  const subscriptionId = getParameterFromURL('subscription_id');
  if (!subscriptionId || !resolvedProductName || !resolvedProductAmount) {
    return;
  }

  handledRef.current = true;
  dispatch(toggleLoader(true));

  const handleReturnFlow = async () => {
    try {
      await EveryDoggyAPIServices.trackPaypalCompletePaymentEvent(
        subscriptionId,
        'paypal',
        resolvedProductName,
        `${resolvedProductAmount}`
      );

      if (isChatUpsalePaypalFlow) {
        const event = GTMOnboardingFunnelEvent.paymentSuccessfulChat(
          `${resolvedProductName}`,
          'paypal',
          `${resolvedProductAmount}`
        );

        analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
      } else {
        const event = GTMQuizFunnelEvent.paymentSuccessful({
          paymentType: 'paypal',
          trialPrice: `${resolvedProductAmount}`,
          productName: `${resolvedProductName}`,
          email: extractEmailFromUrl(),
        });

        analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
      }

      const checkoutResult = await EveryDoggyAPIServices.checkoutPaypalSubscription(subscriptionId);
      const urlProductName = resolvedProductName.replace(/ /g, '_').replace(/\$/g, 'usd');
      const redirectUrl = `${checkoutResult.redirect_url}&ps=paypal&pn=${urlProductName}`;

      window.paypalPayment = true;

      analyticsEventsTracker.trackWithTarget(
        GTMEvent.purchase({
          value: resolvedProductAmount,
        }),
        EVENT_TRACKERS.GTM
      );

      if (isChatUpsalePaypalFlow) {
        dispatch(
          updatePurchaseFlow({
            email: extractEmailFromUrl(),
            url: redirectUrl,
            payment_system: 'paypal',
            screenFlow: ['SuccessScreen'],
            startFromScreen: 'SuccessScreen',
          })
        );
      } else {
        dispatch(
          updatePurchaseFlow({
            email: extractEmailFromUrl(),
            url: redirectUrl,
            payment_system: 'paypal',
          })
        );
      }
    } catch (_error) {
      dispatch(
        showPaymentModal({
          product: resolvedProductName,
          currency,
          price: Number.isFinite(resolvedPriceNumber) ? resolvedPriceNumber : 0,
        })
      );
    } finally {
      dispatch(toggleLoader(false));
      clearPaypalRedirectParams();
    }
  };

  handleReturnFlow();
}
